import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { Bookmark, CalendarPlus, Trash2, X } from "lucide-react";

const ease = [0.22, 1, 0.36, 1];

const esc = (s = "") => s.replace(/([,;])/g, "\\$1");
const pad = (n) => String(n).padStart(2, "0");

const nextSaturday = () => {
  const d = new Date();
  d.setDate(d.getDate() + ((6 - d.getDay() + 7) % 7));
  return d;
};

const downloadIcs = (r) => {
  const day = nextSaturday();
  const date = `${day.getFullYear()}${pad(day.getMonth() + 1)}${pad(day.getDate())}`;
  const dur = r.duration_min || 60;
  const stamp = `${new Date().toISOString().replace(/[-:]/g, "").split(".")[0]}Z`;
  const lines = [
    esc(r.tagline || ""),
    "",
    "Field tasks:",
    ...(r.tasks || []).map((t, i) => `${i + 1}. ${esc(t.text)}`),
    "",
    "Escape your screen. Explore nature.",
  ];
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//EcoRoute//Touch Grass//EN",
    "BEGIN:VEVENT",
    `UID:${r.id}@ecoroute`,
    `DTSTAMP:${stamp}`,
    `DTSTART:${date}T080000`,
    `DURATION:PT${Math.floor(dur / 60)}H${dur % 60}M`,
    `SUMMARY:${esc(`Touch Grass — ${r.name}`)}`,
    `LOCATION:${esc(r.local_region ? `${r.local_region} (${r.region})` : r.region || "")}`,
    `DESCRIPTION:${lines.join("\\n")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
  const url = URL.createObjectURL(new Blob([ics], { type: "text/calendar" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `ecoroute-weekend-${r.id}.ics`;
  a.click();
  URL.revokeObjectURL(url);
  toast.success("Weekend plan downloaded — add it to your calendar.");
};

export default function JournalDrawer({ open, onClose, routes = [], onRemove }) {
  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
            className="fixed inset-0 z-[60] bg-forest-950/70 backdrop-blur-sm"
          />
          <motion.aside
            data-testid="journal-drawer"
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.45, ease }}
            className="fixed right-0 top-0 z-[61] flex h-full w-full max-w-md flex-col border-l border-forest-600/60 bg-forest-900/95 backdrop-blur-xl"
          >
            <div className="flex items-center justify-between border-b border-forest-600/50 px-6 py-5">
              <div>
                <p className="font-mono text-[11px] uppercase tracking-[0.25em] text-moss">
                  Trail journal
                </p>
                <h3 className="mt-1 font-display text-2xl text-cream">My saved routes</h3>
              </div>
              <button
                data-testid="journal-close-button"
                onClick={onClose}
                className="flex h-9 w-9 items-center justify-center rounded-full border border-forest-600/50 text-cream transition-colors hover:border-moss/70 hover:text-moss"
                aria-label="Close journal"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5">
              {routes.length === 0 ? (
                <div className="flex h-full flex-col items-center justify-center gap-3 text-center">
                  <Bookmark className="h-8 w-8 text-forest-500" />
                  <p className="font-display text-lg text-cream">No trails saved yet.</p>
                  <p className="max-w-[240px] text-xs leading-relaxed text-forest-muted">
                    Conjure a route and press “Save to journal” — it'll wait for you here.
                  </p>
                </div>
              ) : (
                <ul className="space-y-3">
                  {routes.map((r) => (
                    <li
                      key={r.id}
                      data-testid="journal-entry"
                      className="rounded-2xl border border-forest-600/50 bg-forest-800/60 p-4"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div>
                          <p className="font-display text-lg leading-snug text-cream">{r.name}</p>
                          <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-forest-muted">
                            {r.distance_km} km · ↑ {r.elevation_m} m · {r.region}
                          </p>
                          <p className="mt-2 text-xs italic text-forest-300">“{r.tagline}”</p>
                          <p className="mt-2 font-mono text-[10px] text-forest-muted">
                            Saved{" "}
                            {new Date(r.savedAt).toLocaleDateString(undefined, {
                              month: "short",
                              day: "numeric",
                            })}
                          </p>
                          <button
                            data-testid={`journal-ics-${r.id}`}
                            onClick={() => downloadIcs(r)}
                            className="mt-3 inline-flex items-center gap-1.5 rounded-full border border-forest-500/60 px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.15em] text-forest-dim transition-colors hover:border-moss/70 hover:text-moss"
                          >
                            <CalendarPlus className="h-3.5 w-3.5" />
                            Weekend plan
                          </button>
                        </div>
                        <button
                          data-testid={`journal-remove-${r.id}`}
                          onClick={() => onRemove(r.id)}
                          className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-forest-600/50 text-forest-muted transition-colors hover:border-ember/60 hover:text-ember"
                          aria-label={`Remove ${r.name}`}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {routes.length > 0 && (
              <div className="border-t border-forest-600/50 px-6 py-4 font-mono text-[11px] text-forest-muted">
                {routes.length} route{routes.length > 1 ? "s" : ""} saved on this device
              </div>
            )}
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
}
